import { FeatureFlagIds } from "../../utils/featureFlagIds";
import React from 'react'
import {useSelector} from 'react-redux';
import {getFullName, hasValue} from "@amsanalytics/react-shared-library";
import {Error, Label} from "@progress/kendo-react-labels";
import {Checkbox, Input, InputChangeEvent} from "@progress/kendo-react-inputs";
import {ComboBox, ComboBoxChangeEvent} from "@progress/kendo-react-dropdowns";
import {uniq} from "ramda";
import { AmsCustomerPreCheckSearchResult } from '../../Interfaces/Common/AmsCustomerPreCheckSearchResult';
import { IPostAmsCustomer, IPreviousAmsCustomerDetailWithNames } from '../../Interfaces/Common/IPostAmsLoan';
import { AppState } from "../../store/rootReducer";
import { SummaryValueRow } from '../ApplicationSummaryCard/ApplicationSummaryCard';
import {
  defaultManualEntryCustomerNumberOption,
  maxAmsCustomerNumberLength
} from "../Views/OrderEditor/AmsLoanPostingDialog/AmsLoanPostingUtils";
import { useConfiguration } from '../../Data/ConfigurationData';

interface CustomerNumberInputsProps {
  customers: IPostAmsCustomer[];
  preCheckSearchResults: AmsCustomerPreCheckSearchResult[];
  handleDesiredCustomerNumberUpdate: (customerId: string, value: string) => void;
  handleSelectCustomerNumberFromAms: (customerId: string, selectedAmsCustomerDetail: IPreviousAmsCustomerDetailWithNames) => void;
  handleNameDifferenceAcknowledgement: (customerId: string, value: boolean) => void;
}

export const CustomerNumberInputsDialog: React.FC<CustomerNumberInputsProps> = (props) => {
  const previousAmsLoans = useSelector((state: AppState) => state.applications.previousAmsLoans);
  const { data: configuration } = useConfiguration();
  const usesFakeSsns = configuration?.featureFlags?.[FeatureFlagIds.UsesFakeSsns] === true; // Client uses fake SSNs, so we can't filter the customer based on that

  const lastnamesDifferBetweenApplicantAndSelectedPreviousAmsCustomer = (selectedPreviousCustomerFromAms: IPreviousAmsCustomerDetailWithNames, lastName: string): boolean => {
    return !!selectedPreviousCustomerFromAms && !selectedPreviousCustomerFromAms.optionForManualEntry && (lastName.toUpperCase().trim() !== selectedPreviousCustomerFromAms.lastName.toUpperCase().trim());
  }
  // If the same value exists more than once, the unique number of customer numbers will be less than the number of customers
  const thereAreMultipleCustomersWithTheSameAmsCustomerNumber = props.customers.length > uniq(props.customers.map(c => c.amsCustomerNumber)).length;

  const thereAreMultipleCustomersWithTheSameAmsCustomerNumberMessage = "Each customer number should be unique";

  const getAlphaNumericCharactersFromString = (value: string) => {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  // Fires whenever user enters a number into the input field.
  const handleDesiredCustomerNumberUpdate = (customerId: string, value: string): void => {
    const filteredValue = getAlphaNumericCharactersFromString(value);
    props.handleDesiredCustomerNumberUpdate(customerId, filteredValue);
  };

  const removeNonNumeric = (value: string) => {
    // Use replace() with a regular expression to remove all non-numeric characters
    return value.replace(/\D/g, '');
  };

  const desiredAmsCustomerNumberAlreadyExists = (desiredAmsCustomerNumber: string | undefined, previousLoansForCustomer: IPreviousAmsCustomerDetailWithNames[]): boolean => {
    return previousLoansForCustomer.some(previousLoan => previousLoan.amsCustomerNumber.trim().toUpperCase() === desiredAmsCustomerNumber?.trim().toUpperCase());
  }

  return (
    <div style={{display: "flex"}}>
      {props.customers.map((c, key) => {
        // Building the list to put in the ComboBox
        const previousLoansForCustomer: IPreviousAmsCustomerDetailWithNames[] = [];
        previousAmsLoans?.forEach(pl => pl.previousCustomers.forEach(pc => {
          // If the client uses fake SSNs (e.g. business-only clients), show all previous loan customers without SSN matching
          const ssnMatch = usesFakeSsns || (pc.ssn && c.ssn && removeNonNumeric(pc.ssn) === removeNonNumeric(c.ssn));
          if (ssnMatch) {
            previousLoansForCustomer.push({
              ...pc,
              summary: `${pc.amsCustomerNumber} - ${getFullName(pc)}`,
              optionForManualEntry: false
            });
          }
        }));

        const dateOfBirthString = hasValue(c.selectedPreviousCustomerFromAms?.dateOfBirth)
          ? new Date(c.selectedPreviousCustomerFromAms!.dateOfBirth!).toLocaleDateString()
          : '';

        const lastnamesDifferAndWeCareAboutIt = lastnamesDifferBetweenApplicantAndSelectedPreviousAmsCustomer(c.selectedPreviousCustomerFromAms, c.lastName);

        const desiredManuallyInputtedAmsCustomerNumberAlreadyExists = desiredAmsCustomerNumberAlreadyExists(c.amsCustomerNumber, previousLoansForCustomer) && c.selectedPreviousCustomerFromAms.optionForManualEntry;
        const preexistingInstancesOfThisCustomerNumber = props.preCheckSearchResults.filter(ac => ac.queriedAmsCustomerNumber.toUpperCase().trim() === c.amsCustomerNumber?.toUpperCase().trim());
        let existingLosCustomerDetailsForThisCustomerNumber: AmsCustomerPreCheckSearchResult | undefined = undefined;

        const existingAmsCustomerDetailsForThisCustomerNumber = preexistingInstancesOfThisCustomerNumber.find(c => hasValue(c.existingAmsCustomerString));
        if (!existingAmsCustomerDetailsForThisCustomerNumber) {
          // If the customer doesn't exist in AMS 2000, we then check to see if the number is reserved by another LOS customer that hasn't been posted
          existingLosCustomerDetailsForThisCustomerNumber = preexistingInstancesOfThisCustomerNumber.find(c => hasValue(c.existingLosCustomerString));
        }
        
        return (
          <div key={key} style={{width: "350px", marginRight: "10px"}}>
            <Label
              style={{color: !!existingAmsCustomerDetailsForThisCustomerNumber ? "red" : "#565656"}}>{`AMS Customer # for ${getFullName(c)}`}
            </Label>
            {previousLoansForCustomer.length > 0 &&
              <ComboBox
                label={'Customer # Options'}
                data={[defaultManualEntryCustomerNumberOption, ...previousLoansForCustomer]}
                textField="summary"
                suggest={true}
                clearButton={false}
                style={{width: "100%"}}
                value={c.selectedPreviousCustomerFromAms}
                onChange={(event: ComboBoxChangeEvent) => {
                  props.handleSelectCustomerNumberFromAms(c.customerId, event.value as IPreviousAmsCustomerDetailWithNames);
                }}
              />
            }
            {(previousLoansForCustomer.length === 0 || c.selectedPreviousCustomerFromAms?.optionForManualEntry) &&
              <Input
                label={"Desired Customer #"}
                valid={!desiredManuallyInputtedAmsCustomerNumberAlreadyExists && !thereAreMultipleCustomersWithTheSameAmsCustomerNumber && c.amsCustomerNumber.length <= maxAmsCustomerNumberLength}
                value={c.amsCustomerNumber}
                type='text'
                onChange={(e: InputChangeEvent) => handleDesiredCustomerNumberUpdate(c.customerId, (e.target.value as string).toUpperCase())}
                style={{width: "100%"}}
                maxLength={maxAmsCustomerNumberLength}
              />
            }
            {c.selectedPreviousCustomerFromAms && !c.selectedPreviousCustomerFromAms.optionForManualEntry &&
              <div style={{display: "flex", flexDirection: "column", marginTop: "10px"}}>
                <SummaryValueRow {...props} title={"Date of Birth"} value={dateOfBirthString}/>
                <SummaryValueRow {...props} title={"Address 1"} value={c.selectedPreviousCustomerFromAms?.fullAddress1}/>
                <SummaryValueRow {...props} title={"Address 2"} value={c.selectedPreviousCustomerFromAms?.fullAddress2}/>
                <SummaryValueRow {...props} title={"Cell Phone"} value={c.selectedPreviousCustomerFromAms?.cellPhoneNumber}/>
                <SummaryValueRow {...props} title={"Phone Number 1"} value={c.selectedPreviousCustomerFromAms?.phoneNumber1}/>
                <SummaryValueRow {...props} title={"Phone Number 2"} value={c.selectedPreviousCustomerFromAms?.phoneNumber2}/>
              </div>
            }
            {c.selectedPreviousCustomerFromAms && lastnamesDifferAndWeCareAboutIt &&
              <div style={{marginTop: "10px"}}>
                <Checkbox
                  label="Confirm that the last name doesn't match between the applicant and the previous AMS Customer"
                  checked={c.confirmSelectedNameIsDifferentThanApplicant}
                  onChange={() => props.handleNameDifferenceAcknowledgement(c.customerId, !c.confirmSelectedNameIsDifferentThanApplicant)}
                />
              </div>
            }
            {existingAmsCustomerDetailsForThisCustomerNumber &&
              <Error>{`The requested customer number already exists in AMS 2000. Customer name- ${existingAmsCustomerDetailsForThisCustomerNumber.existingAmsCustomerString}`}</Error>
            }
            {existingLosCustomerDetailsForThisCustomerNumber &&
              <Error>{`The requested customer number is reserved in the LOS, but not posted to AMS 2000. Customer ${existingLosCustomerDetailsForThisCustomerNumber.existingLosCustomerString}`}</Error>
            }
            {!hasValue(c.amsCustomerNumber) &&
              <Error>{`Please enter a customer number for ${getFullName(c)}`}</Error>
            }
            {desiredManuallyInputtedAmsCustomerNumberAlreadyExists &&
              <Error>{`That AMS customer number already exists for ${getFullName(c)}. Select it from the dropdown list instead.`}</Error>
            }
            {thereAreMultipleCustomersWithTheSameAmsCustomerNumber &&
              <Error>{thereAreMultipleCustomersWithTheSameAmsCustomerNumberMessage}</Error>
            }
          </div>
        )
      })}
    </div>
  )
}